import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { BillLine, UsageAmazon, UsageGrain, UsagePerson, UsageReport, UsageWindow } from '@shared/services'
import { readUsage, readUsageAmazon, readUsageMonth, readUsageWindow, withRetry } from '@shared/services'
import { Morph } from '../components/Morph'
import { StorageCards } from './UsageStorage'
import { KEPT, keep, readKept } from './usageKept'
import '../usage.css'
import { useRemembered } from '../useRemembered'
import { credits, readAllowances, type AllowancePerson } from '@shared/services/allowanceService'

/**
 * Today in New York, as that date at 00:00 UTC (2026-11-04): usage is
 * settled by New York's calendar, and the database hands back each day and
 * month the same way (docs/usage-new-york.sql), so the page's UTC date
 * arithmetic lines up with it.
 */
function newYorkToday(): Date {
  const date = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())
  return new Date(`${date}T00:00:00Z`)
}

/** Spending that is the platform's own, not a person's ('system:descriptions'). */
const isSystem = (email: string) => email.startsWith('system:')

/** A person's monthly allowance in a line: what is left of what, or no limit (2026-10-19). */
function allowanceLine(one: AllowancePerson | undefined, email = ''): string {
  if (isSystem(email)) return "The library's own; not counted against anyone's allowance"
  if (!one) return ''
  if (one.limit === null) return `∞ this month · ${credits(one.used)} credits spent`
  return `${credits(one.left ?? 0)} of ${credits(one.limit)} credits left this month`
}

/**
 * What the platform is spending
 * -------------------------------------------------------------
 * An administrator's page. Two questions, answered in two halves, and they are
 * deliberately not wired to the same controls:
 *
 *   above — how is the company's spending going? Days, months or years.
 *   below — what has one person been doing lately? Always this month, by day.
 *
 * One control driving both would mean choosing "year" and then reading
 * somebody's habits off two data points.
 *
 * COLOUR
 * A single series is white: this product is black and white, and white on
 * black is the most legible mark there is. Colour appears only when the chart
 * is split, and then by FAMILY, not by model -- blue, orange, green and,
 * since Amazon arrived on 2026-09-29, yellow: the palette's own fourth slot,
 * in its fixed order. Checked against this background for red-green and
 * blue-yellow colour blindness on the pairs that actually touch in a stack
 * (worst 8.4, target 8). Yellow and orange never touch -- green is always
 * between them -- and would be too close if they did (4.8), which is why the
 * order of FAMILIES below is part of the design and not a list to re-sort.
 * Anything that fits no family is "Other".
 * 2026-10-12: Nano Banana is yellow and Amazon green, and the two swapped
 * places so the yellow still never touches the orange; Topaz is cyan, last.
 * usage.css has the current order and its check.
 *
 * Inside a family, each model is a lighter step of the family's hue. That is a
 * shade, not an identity: two tints of one blue are not reliably told apart,
 * so which segment is which model is always said in words too -- in the
 * tooltip, and in the breakdown a click opens.
 */

interface Family {
  id: string
  name: string
  color: string
  has: (model: string) => boolean
}

/**
 * The three families, by the model's slug.
 *
 * By prefix rather than by list, so a model added next month lands in its
 * family without anybody editing this page -- and older slugs still in the
 * history (the chat models before gpt-6) are counted where they belong.
 */
const FAMILIES: Family[] = [
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    color: 'var(--use-s1)',
    // The chat's web searches (2026-10-14) are part of what the chat costs.
    has: (m) => (m.startsWith('gpt-') && !m.startsWith('gpt-image')) || m === 'openai-web-search',
  },
  { id: 'gpt-image', name: 'GPT Image', color: 'var(--use-s2)', has: (m) => m.startsWith('gpt-image') },
  // Not a model: searches and products opened through Rainforest, for the
  // Amazon Carousel skill. Recorded in dollars like the rest. Green since
  // 2026-10-12, when Nano Banana took the yellow and the two swapped places
  // in the stack, so the yellow never touches GPT Image's orange.
  { id: 'amazon', name: 'Amazon', color: 'var(--use-s3)', has: (m) => m.startsWith('rainforest') },
  // Yellow (Huanrui, 2026-10-12).
  { id: 'nano', name: 'Nano Banana', color: 'var(--use-s4)', has: (m) => m.startsWith('gemini') },
  // Video Generation (Huanrui, 2026-10-08): a family of its own, deep purple
  // (2026-10-09), the palette's fifth slot, after the yellow. See usage.css.
  { id: 'kling', name: 'Kling', color: 'var(--use-s5)', has: (m) => m.startsWith('kling') },
  // fal.ai (Huanrui, 2026-10-09): Seedance 2.5 video first, more of fal's
  // models later -- give each a "fal-" slug, or add its prefix here. The
  // sixth slot, pink, after Kling (validated 2026-10-09, see usage.css).
  { id: 'fal', name: 'fal', color: 'var(--use-s6)', has: (m) => m.startsWith('fal-') || m.startsWith('seedance') },
  // Topaz Labs, Upscale (Huanrui, 2026-10-12): cyan, the seventh slot, after fal.
  { id: 'topaz', name: 'Topaz', color: 'var(--use-s7)', has: (m) => m === 'topaz' || m.startsWith('topaz-') },
]
const OTHER: Family = { id: 'other', name: 'Other', color: 'var(--use-dim)', has: () => true }

const familyOf = (model: string): Family => FAMILIES.find((f) => f.has(model)) ?? OTHER
/** A family by id, so the providers below do not depend on the order above. */
const family = (id: string): Family => FAMILIES.find((f) => f.id === id) ?? OTHER

/**
 * Who sends the bill. For the figure on the right, which is read against each
 * company's own billing page; the chart keeps the three families. OpenAI's
 * swatch is its two families' colours side by side.
 */
const PROVIDERS: Family[] = [
  {
    id: 'openai',
    name: 'OpenAI',
    color: 'linear-gradient(90deg, var(--use-s1) 50%, var(--use-s2) 50%)',
    has: (m) => family('chatgpt').has(m) || family('gpt-image').has(m),
  },
  { id: 'google', name: 'Nano Banana', color: 'var(--use-s4)', has: (m) => family('nano').has(m) },
  // Rainforest bills a monthly plan; the figure is the plan's price per
  // request times the requests made. See docs/amazon-carousel.sql.
  { id: 'rainforest', name: 'Amazon', color: 'var(--use-s3)', has: (m) => family('amazon').has(m) },
  // Topaz Labs, for Upscale (2026-10-05): its credits at the price in
  // paid_services (docs/upscale.sql). Cyan, its own family since 2026-10-12.
  { id: 'topaz', name: 'Topaz', color: 'var(--use-s7)', has: (m) => family('topaz').has(m) },
  // Kling billed on its own until 2026-10-27, when it moved to fal (whose
  // "fal-kling-" rows are fal's): kept for the months before, shown only
  // where it spent something.
  { id: 'kling', name: 'Kling', color: 'var(--use-s5)', has: (m) => family('kling').has(m) },
  // fal bills on its own too, like Kling.
  { id: 'fal', name: 'fal', color: 'var(--use-s6)', has: (m) => family('fal').has(m) },
]
const providerOf = (model: string): Family => PROVIDERS.find((p) => p.has(model)) ?? OTHER

/** What people call them. The slug is the fallback for anything not listed. */
const MODEL_NAMES: Record<string, string> = {
  'gpt-6-luna': 'Luna',
  'gpt-6-sol': 'Sol',
  'gpt-6-astra': 'Astra',
  'gpt-5.6-luna': 'Luna 5.6',
  'gpt-5.6-sol': 'Sol 5.6',
  'gpt-5.6-terra': 'Terra 5.6',
  'gpt-image-2': 'Image 2',
  'gpt-image-2.5-sunburst': 'Sunburst',
  'gpt-image-2.5-flare': 'Flare',
  'gemini-nano-banana-2.1': 'Nano Banana 2.1',
  'gemini-3.1-flash-image': 'Nano Banana 2',
  'gemini-3-pro-image': 'Nano Banana Pro',
  // Reading the text on a picture for Edit text (routes/canvasText.ts).
  'gemini-3.8-flash': 'Gemini Flash (text reading)',
  'rainforest-search': 'Amazon search',
  'rainforest-product': 'Amazon product',
  // Every Topaz model arrives as one slug, "topaz" (usageService, modelSlug).
  topaz: 'Upscale',
  // Video Generation (workers/api/src/video.ts), 2026-10-08.
  'kling-3.0': 'Kling 3.0 1080p (video)',
  'kling-3.0-4k': 'Kling 3.0 4K (video)',
  // Through fal since 2026-10-27 (video.ts).
  'fal-kling-o3-4k': 'Kling O3 4K (video)',
  'fal-kling-v4-1080p': 'Kling v4 1080p (video)',
  'seedance-2.5': 'Seedance 2.5 1080p (video)',
  // The board's Angle card (routes/angles.ts), 2026-10-09.
  'fal-flux-2-angles': 'FLUX 2 Angle (picture)',
  // The Vectorize card's paid way (routes/vectorize.ts), 2026-10-09.
  'fal-recraft-vectorize': 'Recraft Vectorize (SVG)',
  // Expand on the board (routes/expand.ts), 2026-10-26.
  'fal-bria-expand': 'Bria Expand (picture)',
  // The Background and Eraser cards (routes/bria.ts), 2026-10-27.
  'fal-bria-rmbg': 'Bria RMBG 2.0 (background)',
  'fal-bria-bg-replace': 'Bria Background Replace',
  'fal-bria-eraser': 'Bria Eraser (picture)',
  // Tried as Expand's "Pro" on 2026-10-26 and taken out; named for its rows.
  'fal-flux-fill-pro': 'FLUX Fill (picture)',
}
const modelName = (slug: string): string => MODEL_NAMES[slug] ?? slug

/**
 * The k-th model of a family: the family hue, then lighter steps of it.
 *
 * Lighter rather than darker, because the page is black: a darker step of a
 * mid-tone blue sinks into the background, a lighter one stays readable.
 */
const SHADES = [100, 68, 44, 26]
const shadeOf = (family: Family, k: number): string => {
  const keep = SHADES[Math.min(k, SHADES.length - 1)]
  return keep === 100 ? family.color : `color-mix(in oklab, ${family.color} ${keep}%, #ffffff)`
}

const GRAINS: { id: UsageGrain; label: string; since: string }[] = [
  { id: 'day', label: 'Day', since: 'last 30 days' },
  { id: 'month', label: 'Month', since: 'last 6 months' },
  { id: 'year', label: 'Year', since: 'all time' },
]

const money = (v: number): string =>
  v >= 1000 ? `$${Math.round(v).toLocaleString()}` : v >= 100 ? `$${v.toFixed(0)}` : `$${v.toFixed(2)}`

/**
 * An axis ends on a number somebody would say out loud.
 *
 * Left as the data's own maximum, the top gridline reads $2.59 and every line
 * below it stops being a quantity anybody can hold in their head.
 */
function niceMax(v: number): number {
  if (v <= 0) return 1
  const step = v / 4
  const magnitude = Math.pow(10, Math.floor(Math.log10(step)))
  const nice = [1, 2, 2.5, 5, 10].find((n) => n * magnitude >= step) ?? 10
  return nice * magnitude * 4
}

function dayLabel(iso: string, grain: UsageGrain): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  if (grain === 'year') return String(d.getUTCFullYear())
  if (grain === 'month') return d.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' })
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

interface Series {
  name: string
  color: string
  values: number[]
  /** Which family a model's series belongs to, when the chart is split. */
  family?: Family
}

interface Hover {
  x: number
  y: number
  title: string
  lines: { color: string; name: string; value: string }[]
}

/* ------------------------------------------------------------------ */

export function UsagePage() {
  const [grain, setGrain] = useState<UsageGrain>('day')
  const [split, setSplit] = useState(false)
  // Bars by default; the table view was taken out (Huanrui, 2026-10-01).
  const [shape, setShape] = useState<'line' | 'bar'>('bar')
  const [showBudget, setShowBudget] = useState(true)

  const [report, setReport] = useState<UsageReport | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(true)
  const [hover, setHover] = useState<Hover | null>(null)

  /** Bumped to read the page again: when Amazon's period has just been billed. */
  const [reread, setReread] = useState(0)

  useEffect(() => {
    let cancelled = false
    // What this browser saw last time, at once (2026-10-05); the request
    // below then replaces it quietly. A first visit still waits.
    const seen = readKept<UsageReport>(`${KEPT}:${grain}`)
    if (seen) setReport(seen)
    setBusy(true)
    setError('')
    withRetry(() => readUsage(grain))
      .then((found) => {
        if (cancelled) return
        setReport(found)
        keep(`${KEPT}:${grain}`, found)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'That could not be read')
      })
      .finally(() => {
        if (!cancelled) setBusy(false)
      })
    return () => {
      cancelled = true
    }
  }, [grain, reread])

  /*
   * Amazon's count, on its own (2026-10-05): it is a call out to Rainforest
   * and used to hold the whole page up. Its row fills in when it arrives;
   * until then it shows the last count this browser saw, if any.
   */
  const [amazon, setAmazon] = useState<UsageAmazon | null>(() => readKept<UsageAmazon>(`${KEPT}:amazon`))
  useEffect(() => {
    let cancelled = false
    readUsageAmazon()
      .then(({ amazon: found, closed }) => {
        if (cancelled) return
        setAmazon(found)
        if (found) keep(`${KEPT}:amazon`, found)
        // A period's extra was just written into the bill: read the bills again.
        if (closed) setReread((n) => n + 1)
      })
      .catch((err: unknown) => console.warn('Amazon count not read', err))
    return () => {
      cancelled = true
    }
  }, [])

  /* ---------- the chart at the top ---------- */

  const buckets = useMemo(() => report?.overall.map((p) => p.at) ?? [], [report])
  const labels = useMemo(() => buckets.map((at) => dayLabel(at, grain)), [buckets, grain])

  /**
   * One series per model, grouped by family, each in its family's shade.
   *
   * Families in their fixed order, and within a family the biggest spender
   * first -- so it gets the full hue and the smaller ones the lighter steps.
   * Colour follows the model's place in its family, never its rank overall.
   */
  /**
   * Every model's colour, decided once for the whole page.
   *
   * So a model is the same shade at the top and in a person's charts below --
   * colour follows the model, never where it happens to rank in one chart.
   * Ranked within its family by spend over the period, then this month, and
   * models that spent nothing are left out altogether: the stand-in skills
   * write $0 rows, and an "Other" in the legend holding nothing but zeros is
   * noise.
   */
  const shades = useMemo(() => {
    const spend = new Map<string, number>()
    for (const row of report?.byModel ?? []) spend.set(row.model, (spend.get(row.model) ?? 0) + row.cost)
    // Smaller weights after the period's own spend, so they rank behind it
    // and only decide the shade of a model the chart above does not show.
    for (const row of report?.personModels ?? []) spend.set(row.key, (spend.get(row.key) ?? 0) + row.cost * 1e-6)
    for (const row of report?.last30?.personModels ?? []) spend.set(row.model, (spend.get(row.model) ?? 0) + row.cost * 1e-9)
    return makeShades(spend)
  }, [report])

  /**
   * One series per model, grouped by family, each in its family's shade.
   * Families in their fixed order; within one, the biggest spender first.
   */
  const modelSeries: Series[] = useMemo(() => {
    if (!report || !split) return []
    return seriesByModel(report.byModel, buckets, shades)
  }, [report, split, buckets, shades])

  /** The same, added up to a family each. What the lines and the legend show. */
  const familySeries: Series[] = useMemo(() => {
    const out: Series[] = []
    for (const family of [...FAMILIES, OTHER]) {
      const mine = modelSeries.filter((one) => one.family === family)
      if (mine.length === 0) continue
      out.push({
        name: family.name,
        family,
        color: family.color,
        values: buckets.map((_, i) => mine.reduce((sum, one) => sum + (one.values[i] ?? 0), 0)),
      })
    }
    return out
  }, [modelSeries, buckets])

  const series: Series[] = useMemo(() => {
    if (!report) return []
    if (!split) {
      return [{ name: 'Total', color: 'var(--use-mark)', values: report.overall.map((p) => p.cost) }]
    }
    // Bars stack every model, shaded within its family; lines would be a
    // tangle of eight, so they show the three families.
    return shape === 'line' ? familySeries : modelSeries
  }, [report, split, shape, familySeries, modelSeries])

  /**
   * What the figure on the right is about: everything, or one PROVIDER.
   *
   * By who sends the bill, not by family -- OpenAI (ChatGPT and GPT Image
   * together) and Google (Nano Banana) -- because the question this figure
   * answers is "does it match that company's billing page". Huanrui,
   * 2026-09-28. The chart keeps its three families.
   *
   * Its own control and not the chart's split: somebody checking a bill
   * should not have to redraw the chart to get the number.
   */
  const [focus, setFocus] = useState<string>('all')

  /**
   * Each provider's spend THIS MONTH, and each of its models'. Every figure in
   * this card is this month's, whichever button is pressed (Huanrui,
   * 2026-10-01) -- the chart beside it is what follows Day / Month / Year.
   * Added up from everybody's models this month (usage_report's personModels).
   */
  const familyTotals = useMemo(() => {
    const byFamily = new Map<string, { family: Family; total: number; models: Map<string, number> }>()
    for (const row of report?.personModels ?? []) {
      if (row.cost <= 0) continue
      const family = providerOf(row.key)
      const entry = byFamily.get(family.id) ?? { family, total: 0, models: new Map<string, number>() }
      entry.total += row.cost
      entry.models.set(row.key, (entry.models.get(row.key) ?? 0) + row.cost)
      byFamily.set(family.id, entry)
    }
    // OpenAI and Nano Banana are always there, at $0.00 in a month with none
    // of theirs yet; Amazon (billed by subscription now) and Other only when
    // they spent something.
    // Kling too since 2026-10-08: a provider on the same footing as OpenAI.
    // fal too since 2026-10-09.
    // Kling no longer (2026-10-27): it is fal's now, and shows only in a month it spent in.
    const always = new Set(['openai', 'google', 'fal'])
    return [...PROVIDERS, OTHER]
      .map((f) => byFamily.get(f.id) ?? (always.has(f.id) ? { family: f, total: 0, models: new Map<string, number>() } : undefined))
      .filter((one): one is { family: Family; total: number; models: Map<string, number> } => Boolean(one))
  }, [report])
  const focused = familyTotals.find((one) => one.family.id === focus) ?? null

  /*
   * WITH SUBSCRIPTIONS (Huanrui, 2026-10-01). On, the figure is the whole
   * monthly bill: what the models cost this month, plus what the platform
   * costs every month whether or not anybody uses it, plus Amazon requests
   * past what its plan includes. On by default; remembered once changed.
   * Only for "All" -- a single provider's figure is that provider's usage.
   *
   * Subscriptions count from October 2026 (subscriptions.ts): the Worker
   * writes each month's into its bill on the 1st, and `report.subscriptions`
   * is this month's. Amazon's extra requests go by Rainforest's own period
   * and land in the bill of the month a period ends in, so this month's
   * figure has only what is already written there; the period still running
   * is shown beside it, not added.
   */
  const [withSubscriptions, setWithSubscriptions] = useRemembered('ww-usage-subscriptions', true)
  const subscriptionsTotal = (report?.subscriptions ?? []).reduce((sum, one) => sum + one.usd, 0)
  const allIn = withSubscriptions && !focused && Boolean(report)
  /** Amazon extra already in this month's bill: periods that ended this month. */
  const amazonBilled = useMemo(
    () => (report?.bills ?? []).filter((line) => line.month === report?.thisMonth && line.kind === 'amazon'),
    [report],
  )
  const amazonBilledTotal = amazonBilled.reduce((sum, line) => sum + line.usd, 0)
  const monthAllIn = (report?.month.cost ?? 0) + subscriptionsTotal + amazonBilledTotal

  /**
   * Every month's bill, newest first: the month still running, added up from
   * today's figures, then each month the Worker has written down
   * (monthly_bills), which never changes once written.
   */
  const months = useMemo(() => {
    if (!report) return [] as MonthBill[]
    const byMonth = new Map<string, BillLine[]>()
    for (const line of report.bills) byMonth.set(line.month, [...(byMonth.get(line.month) ?? []), line])
    const out: MonthBill[] = []
    if (report.thisMonth) {
      out.push({
        month: report.thisMonth,
        live: true,
        lines: [
          { month: report.thisMonth, item: 'Models', kind: 'models', usd: report.month.cost, detail: '' },
          ...report.subscriptions.map((one) => ({
            month: report.thisMonth,
            item: one.name,
            kind: 'subscription' as const,
            usd: one.usd,
            detail: one.note,
          })),
          ...amazonBilled,
        ],
        total: monthAllIn,
      })
    }
    for (const [month, lines] of byMonth) {
      if (month === report.thisMonth) continue
      out.push({ month, live: false, lines, total: lines.reduce((sum, one) => sum + one.usd, 0) })
    }
    return out.sort((a, b) => b.month.localeCompare(a.month))
  }, [report, monthAllIn, amazonBilled])

  /** How many people spent something this month. */
  const peopleThisMonth = useMemo(() => new Set((report?.personModels ?? []).map((row) => row.email)).size, [report])
  const dayOfMonth = newYorkToday().getUTCDate()

  /** The bucket a click opened, for the breakdown under the chart. */
  const [picked, setPicked] = useState<number | null>(null)
  useEffect(() => setPicked(null), [grain, split])

  /*
   * Where the budget line sits for this grain.
   *
   * A month's budget on a chart of days is not the month's number — it is what
   * one day may have if every day is to be equal. Saying which in the label is
   * the difference between a line people act on and a line they mistrust.
   */
  const budgetLine = useMemo(() => {
    const monthly = report?.budget
    if (!monthly || !showBudget) return null
    const today = newYorkToday()
    const daysThisMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0)).getUTCDate()
    if (grain === 'day') return { value: monthly / daysThisMonth, label: `${money(monthly / daysThisMonth)} / day` }
    if (grain === 'month') return { value: monthly, label: `${money(monthly)} / month` }
    return { value: monthly * 12, label: `${money(monthly * 12)} / year` }
  }, [report, showBudget, grain])

  /* ---------- people, and the monthly bills ---------- */

  /**
   * The person cards: this month, New York's (2026-11-04), most spent first
   * -- the same month as the credits line on each card. Opened, a card still
   * shows the person's last 30 days. Before docs/usage-new-york.sql there is
   * no month list, and the last 30 days stand in, as before.
   */
  const people: UsagePerson[] = report?.monthPeople ?? report?.last30?.people ?? report?.people ?? []
  // Everybody's monthly allowance (AllowancesPage), for the person cards.
  const [allowances, setAllowances] = useState<Map<string, AllowancePerson>>(new Map())
  useEffect(() => {
    readAllowances()
      .then((found) => setAllowances(new Map(found.people.map((one) => [one.email.toLowerCase(), one]))))
      .catch(() => setAllowances(new Map()))
  }, [])

  /** Which card is open full size, if any. */
  const [open, setOpen] = useState<{ key: string; kind: 'person' | 'month'; id: string } | null>(null)
  const closeOpen = useCallback(() => setOpen(null), [])
  const [billSearch, setBillSearch] = useState('')

  const move = useCallback((event: React.PointerEvent, next: Omit<Hover, 'x' | 'y'>) => {
    setHover({ x: event.clientX, y: event.clientY, ...next })
  }, [])

  return (
    <div className="use" onPointerLeave={() => setHover(null)}>
      <header className="use__head">
        <div className="use__seg" role="group" aria-label="Period">
          {GRAINS.map((g) => (
            <button
              key={g.id}
              type="button"
              aria-pressed={grain === g.id}
              onClick={() => setGrain(g.id)}
            >
              {g.label}
            </button>
          ))}
        </div>

        <div className="use__seg" role="group" aria-label="Chart type">
          {(['bar', 'line'] as const).map((s) => (
            <button key={s} type="button" aria-pressed={shape === s} onClick={() => setShape(s)}>
              {s === 'line' ? 'Line' : 'Bar'}
            </button>
          ))}
        </div>

        <select
          className="use__select"
          value={split ? 'split' : 'all'}
          onChange={(event) => setSplit(event.target.value === 'split')}
          aria-label="How to split the chart"
        >
          <option value="all">All models</option>
          <option value="split">Split by family</option>
        </select>

        {report?.budget != null ? (
          <div className="use__seg">
            <button type="button" aria-pressed={showBudget} onClick={() => setShowBudget((on) => !on)}>
              Budget
            </button>
          </div>
        ) : null}
      </header>

      {error ? <div className="ww-error">{error}</div> : null}

      <div className="use__row use__row--top">
        <section className="use__card">
          <div className="use__card-head">
            <span className="use__label">Spend over time</span>
            <span className="use__spacer" />
            {series.length > 1 ? (
              <span className="use__legend">
                {(split ? familySeries : series).map((s) => (
                  <span key={s.name}>
                    <i style={{ background: s.color }} />
                    {s.name}
                  </span>
                ))}
              </span>
            ) : null}
          </div>

          {busy && !report ? (
            <p className="use__quiet">Reading…</p>
          ) : (
            <Plot
              labels={labels}
              series={series}
              bars={shape === 'bar'}
              stacked={split && shape === 'bar'}
              picked={split ? picked : null}
              onPick={split ? setPicked : undefined}
              budget={budgetLine}
              onHover={move}
              onLeave={() => setHover(null)}
            />
          )}

          {split ? (
            picked !== null && labels[picked] !== undefined ? (
              <Breakdown
                title={labels[picked]}
                models={modelSeries}
                index={picked}
                onClose={() => setPicked(null)}
              />
            ) : (
              <p className="use__note">Click a {grain} to see what each model spent.</p>
            )
          ) : null}
        </section>

        <section className="use__card use__total">
          {/* Apart from the provider buttons, top right (Huanrui, 2026-10-01). */}
          <button
            type="button"
            className="use__subs"
            aria-pressed={withSubscriptions}
            onClick={() => setWithSubscriptions(!withSubscriptions)}
            title="Add what the platform costs every month to what the models cost"
            style={focused ? { visibility: 'hidden' } : undefined}
          >
            + Subscriptions
          </button>
          <div>
            <span className="use__label">
              {focused ? `${focused.family.name} spend` : allIn ? 'Platform spend' : 'Model spend'}
            </span>
            {familyTotals.length > 0 ? (
              <div className="use__seg use__seg--focus" role="group" aria-label="Which spend">
                <button type="button" aria-pressed={!focused} onClick={() => setFocus('all')}>
                  All
                </button>
                {familyTotals.map(({ family }) => (
                  <button
                    key={family.id}
                    type="button"
                    aria-pressed={focused?.family.id === family.id}
                    onClick={() => setFocus(family.id)}
                  >
                    <i style={{ background: family.color }} aria-hidden="true" />
                    {family.name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="use__figure">
              {money(focused ? focused.total : allIn ? monthAllIn : (report?.month.cost ?? 0))}
            </div>
            <div className="use__quiet">{report ? `${monthLabel(report.thisMonth)} so far` : 'This month'}</div>

            {report ? (
              report.budget != null ? (
                <div className="use__meter">
                  <div className="use__meter-track">
                    <span
                      className={`use__meter-fill${report.month.cost > report.budget ? ' use__meter-fill--over' : ''}`}
                      style={{ width: `${Math.min(100, (report.month.cost / report.budget) * 100).toFixed(1)}%` }}
                    />
                  </div>
                  <div className="use__quiet">
                    {money(report.month.cost)} of <b>{money(report.budget)}</b> this month
                  </div>
                </div>
              ) : null
            ) : null}
          </div>

          {focused ? (
            <dl className="use__rows">
              <Row label="Per day, average" value={money(focused.total / Math.max(1, dayOfMonth))} />
              {[...focused.models.entries()]
                .sort((a, b) => b[1] - a[1])
                .map(([model, cost]) => (
                  <Row key={model} label={modelName(model)} value={money(cost)} />
                ))}
            </dl>
          ) : allIn && report ? (
            <dl className="use__rows">
              <Row label="Models" value={money(report.month.cost)} />
              {report.subscriptions.map((one) => (
                <Row key={one.name} label={one.name} value={money(one.usd)} />
              ))}
              {/*
               * Amazon extra: what is in this month's bill (periods that ended
               * this month), with the period still running beside it as a note
               * -- requests left, or what it has gone over so far.
               */}
              {/* Amazon: just how many requests are left this period (2026-10-13).
                  Past the 500, how many over, in the warning colour. */}
              <Row
                label="Amazon"
                value={
                  !amazon
                    ? '—'
                    : amazon.extra > 0
                      ? `${amazon.extra.toLocaleString()} over`
                      : `${(amazon.remaining ?? 0).toLocaleString()} left`
                }
                warn={(amazon?.extra ?? 0) > 0}
              />
            </dl>
          ) : (
            <dl className="use__rows">
              {familyTotals.map(({ family, total }) => (
                <Row key={family.id} label={family.name} value={money(total)} />
              ))}
              <Row label="Per day, average" value={money((report?.month.cost ?? 0) / Math.max(1, dayOfMonth))} />
              <Row label="Calls" value={(report?.month.calls ?? 0).toLocaleString()} />
              <Row label="People" value={String(peopleThisMonth)} />
            </dl>
          )}
        </section>
      </div>

      <div className="use__row use__row--top use__row--lower">
        <section className="use__card">
          <div className="use__card-head">
            <span className="use__label">{report?.monthPeople ? 'People · this month' : 'People · last 30 days'}</span>
          </div>
          {people.length > 0 ? (
            <div className="use__cards">
              {people.map((p) => {
                const key = `person:${p.email}`
                return (
                  <button
                    key={p.email}
                    type="button"
                    className="use__pcard"
                    data-card={key}
                    style={open?.key === key ? { visibility: 'hidden' } : undefined}
                    onClick={() => setOpen({ key, kind: 'person', id: p.email })}
                  >
                    <CardFace title={p.name} value={money(p.cost)} sub={allowanceLine(allowances.get(p.email.toLowerCase()), p.email)} />
                  </button>
                )
              })}
            </div>
          ) : (
            <p className="use__quiet">{busy ? 'Reading…' : report?.monthPeople ? 'Nothing spent this month.' : 'Nothing spent in the last 30 days.'}</p>
          )}
        </section>

        <section className="use__card use__bills">
          <div className="use__bills-in">
            <div className="use__card-head">
              <span className="use__label">Monthly bills</span>
              <span className="use__spacer" />
              {/* Everything the platform has cost: every month's bill, this one so far included. */}
              {months.length > 0 ? (
                <span className="use__bills-total" title="Every month's bill added up, this month so far included">
                  All time {money(months.reduce((sum, one) => sum + one.total, 0))}
                </span>
              ) : null}
            </div>
            <input
              className="use__search"
              type="search"
              placeholder="Search months"
              value={billSearch}
              onChange={(event) => setBillSearch(event.target.value)}
              aria-label="Search months"
            />
            <div className="use__bill-list">
              {months
                .filter((one) => {
                  const q = billSearch.trim().toLowerCase()
                  return !q || monthLabel(one.month).toLowerCase().includes(q) || one.month.includes(q)
                })
                .map((one) => {
                  const key = `month:${one.month}`
                  return (
                    <button
                      key={one.month}
                      type="button"
                      className="use__bill"
                      data-card={key}
                      style={open?.key === key ? { visibility: 'hidden' } : undefined}
                      onClick={() => setOpen({ key, kind: 'month', id: one.month })}
                    >
                      <span>
                        {monthLabel(one.month)}
                        {one.live ? <em>so far</em> : null}
                      </span>
                      <b>{money(one.total)}</b>
                    </button>
                  )
                })}
              {months.length === 0 && !busy ? <p className="use__quiet">No bills yet.</p> : null}
            </div>
          </div>
        </section>
      </div>

      {/* What is stored (Huanrui, 2026-10-05): its own row under the people
          and the bills, Supabase on the left and R2 on the right
          (UsageStorage.tsx). */}
      <StorageCards onHover={move} onLeave={() => setHover(null)} />

      {open && report ? (
        <Morph
          key={open.kind}
          source={() => document.querySelector<HTMLElement>(`[data-card="${CSS.escape(open.key)}"]`)}
          label={open.kind === 'person' ? 'Person' : 'Month'}
          onClosed={closeOpen}
          face={
            open.kind === 'person' ? (
              <CardFace
                title={people.find((p) => p.email === open.id)?.name ?? open.id}
                value={money(people.find((p) => p.email === open.id)?.cost ?? 0)}
                sub={allowanceLine(allowances.get(open.id.toLowerCase()), open.id)}
              />
            ) : (
              <CardFace
                title={monthLabel(open.id)}
                value={money(months.find((m) => m.month === open.id)?.total ?? 0)}
              />
            )
          }
        >
          {open.kind === 'person' ? (
            <PersonView
              window={report.last30}
              email={open.id}
              fallback={people.find((p) => p.email === open.id) ?? null}
              allowance={allowanceLine(allowances.get(open.id.toLowerCase()), open.id)}
              shades={shades}
              onHover={move}
              onLeave={() => setHover(null)}
            />
          ) : (
            <MonthView
              bill={months.find((m) => m.month === open.id) ?? null}
              month={open.id}
              months={months.map((m) => m.month)}
              onMonth={(month) => setOpen({ key: `month:${month}`, kind: 'month', id: month })}
              shades={shades}
              onHover={move}
              onLeave={() => setHover(null)}
            />
          )}
        </Morph>
      ) : null}

      {hover ? (
        <div className="use__tip" style={{ left: hover.x, top: hover.y }}>
          <u>{hover.title}</u>
          {hover.lines.map((l, i) => (
            <em key={i}>
              <i style={{ background: l.color }} />
              {l.name} <b>{l.value}</b>
            </em>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * Rows of (bucket, model, cost) into one series per model, in family order.
 *
 * Only models in `shades` -- the ones that spent something -- and within a
 * family in the order their shades were handed out, so the full hue sits at
 * the bottom of every stack.
 */
function seriesByModel(
  rows: { at: string; model: string; cost: number }[],
  buckets: string[],
  shades: Map<string, { family: Family; color: string }>,
): Series[] {
  const present = new Set(rows.filter((r) => r.cost > 0).map((r) => r.model))
  const key = (at: string) => at.slice(0, 10)
  const out: Series[] = []
  for (const [model, look] of shades) {
    if (!present.has(model)) continue
    out.push({
      name: modelName(model),
      family: look.family,
      color: look.color,
      values: buckets.map((at) =>
        rows
          .filter((r) => r.model === model && key(r.at) === key(at))
          .reduce((sum, r) => sum + r.cost, 0),
      ),
    })
  }
  return out
}

/**
 * One bucket, broken down: each family's total, and every model inside it.
 *
 * The words that go with the shades. Two tints of one hue are not reliably
 * told apart, so the chart's shading is a hint and this is the answer.
 */
function Breakdown({
  title,
  models,
  index,
  onClose,
}: {
  title: string
  models: Series[]
  index: number
  onClose: () => void
}) {
  const families = [...FAMILIES, OTHER]
    .map((family) => {
      const mine = models
        .filter((one) => one.family === family)
        .map((one) => ({ name: one.name, color: one.color, cost: one.values[index] ?? 0 }))
      return { family, mine, total: mine.reduce((sum, one) => sum + one.cost, 0) }
    })
    .filter((one) => one.mine.length > 0)
  const all = families.reduce((sum, one) => sum + one.total, 0)

  return (
    <div className="use__breakdown">
      <div className="use__breakdown-head">
        <b>{title}</b>
        <span className="use__quiet">{money(all)}</span>
        <span className="use__spacer" />
        <button type="button" className="use__breakdown-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      <div className="use__breakdown-grid">
        {families.map(({ family, mine, total }) => (
          <div key={family.id} className="use__breakdown-family">
            <div className="use__breakdown-line use__breakdown-line--family">
              <i style={{ background: family.color }} />
              <span>{family.name}</span>
              <b>{money(total)}</b>
            </div>
            {mine.map((one) => (
              <div key={one.name} className="use__breakdown-line">
                <i style={{ background: one.color }} />
                <span>{one.name}</span>
                <b>{money(one.cost)}</b>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function Row({
  label,
  value,
  warn,
  note,
  noteWarn,
}: {
  label: string
  value: string
  warn?: boolean
  /** A quiet word after the label, such as how many requests are left. */
  note?: string
  noteWarn?: boolean
}) {
  return (
    <div className={`use__row-line${warn ? ' use__row-line--warn' : ''}`}>
      <dt>
        {label}
        {note ? <small className={noteWarn ? 'use__row-note use__row-note--warn' : 'use__row-note'}>{note}</small> : null}
      </dt>
      <dd>{value}</dd>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * One plot, doing every job on this page.
 *
 * Written by hand rather than pulled in: four small charts do not justify a
 * charting library, its bundle, or its opinions about what a chart looks like
 * — and the one thing they all need, a mark that reads on black, is exactly
 * what a library's defaults get wrong.
 * ------------------------------------------------------------------ */

interface PlotProps {
  labels: string[]
  series: Series[]
  height?: number
  bars?: boolean
  /** Categories sit in slots, so their labels belong under the slot centres. */
  slots?: boolean
  /** Write the value above each bar. Only where there are few enough. */
  label?: boolean
  budget?: { value: number; label: string } | null
  /**
   * Bars stacked one model on another, rather than side by side. For the
   * split view, where eight models side by side in a day's slot would each
   * be a hair wide.
   */
  stacked?: boolean
  /** The bucket a click has opened, drawn behind so it is findable. */
  picked?: number | null
  /** Given, a column can be clicked. */
  onPick?: (index: number) => void
  onHover: (event: React.PointerEvent, next: Omit<Hover, 'x' | 'y'>) => void
  onLeave: () => void
}

function Plot({
  labels,
  series,
  height = 280,
  bars,
  slots,
  label,
  budget,
  stacked,
  picked,
  onPick,
  onHover,
  onLeave,
}: PlotProps) {
  const width = 920
  const padL = 46
  const padR = 10
  const padT = 14
  const padB = 26
  const iw = width - padL - padR
  const ih = height - padT - padB

  // Stacked, the tallest thing is a bucket's total, not any one series.
  const peak = Math.max(
    ...(stacked
      ? labels.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0))
      : series.flatMap((s) => s.values)),
    budget?.value ?? 0,
    0,
  )
  const max = niceMax(peak * 1.08)
  const X = (i: number) => padL + (labels.length <= 1 ? iw / 2 : (iw * i) / (labels.length - 1))
  const slotX = (i: number) => padL + (iw / Math.max(1, labels.length)) * (i + 0.5)
  const Y = (v: number) => padT + ih - ih * (v / max)

  const every = labels.length > 14 ? Math.ceil(labels.length / 8) : 1
  if (labels.length === 0) return <p className="use__quiet">Nothing in this period.</p>

  return (
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Spend" onPointerLeave={onLeave}>
      {[0, 1, 2, 3, 4].map((i) => {
        const y = padT + ih - (ih * i) / 4
        return (
          <g key={i}>
            <line className={i ? 'use__grid' : 'use__zero'} x1={padL} y1={y} x2={width - padR} y2={y} />
            <text className="use__tick use__tick--y" x={padL - 8} y={y + 3.5}>
              {i ? money((max * i) / 4) : '0'}
            </text>
          </g>
        )
      })}

      {labels.map((l, i) =>
        i % every ? null : (
          // Bars sit in slots, so their labels go under the slot centres. They
          // were under the line chart's points instead, which drifts further
          // from each bar towards either end of the axis.
          <text key={i} className="use__tick" x={slots || bars ? slotX(i) : X(i)} y={height - 7} textAnchor="middle">
            {l}
          </text>
        ),
      )}

      {budget ? (
        <>
          <line className="use__budget" x1={padL} y1={Y(budget.value)} x2={width - padR} y2={Y(budget.value)} />
          <text className="use__budget-label" x={width - padR} y={Y(budget.value) - 8} textAnchor="end">
            Budget · {budget.label}
          </text>
        </>
      ) : null}

      {picked !== null && picked !== undefined && picked < labels.length ? (
        <rect
          className="use__picked"
          x={(slots || bars ? slotX(picked) : X(picked)) - iw / Math.max(1, labels.length) / 2}
          y={padT}
          width={iw / Math.max(1, labels.length)}
          height={ih}
        />
      ) : null}

      {bars && stacked
        ? labels.map((_, i) => {
            /*
             * One column per bucket, the models piled in family order.
             *
             * A 2px gap of background between segments, so two shades of the
             * same hue meet at a line rather than blending into one block.
             */
            const slot = iw / Math.max(1, labels.length)
            const wide = Math.max(3, Math.min(40, slot - 8))
            const x = slotX(i) - wide / 2
            let below = 0
            let floor = padT + ih
            const pieces: JSX.Element[] = []
            series.forEach((s, si) => {
              const v = s.values[i] ?? 0
              if (v <= 0) return
              const top = Y(below + v)
              const gap = pieces.length > 0 ? 2 : 0
              pieces.push(
                <rect
                  key={`${si}-${i}`}
                  className="use__bar"
                  style={{ fill: s.color }}
                  x={x}
                  y={top}
                  width={wide}
                  height={Math.max(1, floor - top - gap)}
                  rx={2}
                />,
              )
              below += v
              floor = top
            })
            if (label && below > 0) {
              pieces.push(
                <text key={`total-${i}`} className="use__tick" x={x + wide / 2} y={floor - 7} textAnchor="middle">
                  {money(below)}
                </text>,
              )
            }
            return <g key={`stack-${i}`}>{pieces}</g>
          })
        : bars
        ? series.map((s, si) => {
            const slot = iw / Math.max(1, labels.length)
            const wide = slots
              ? Math.min(64, slot - 16)
              : Math.max(3, (slot - 6) / series.length - 2)
            return s.values.map((v, i) => {
              const x = slots ? slotX(i) - wide / 2 : padL + slot * i + 3 + si * (wide + 2)
              const y = Y(v)
              return (
                <g key={`${si}-${i}`}>
                  <rect
                    className="use__bar"
                    style={{ fill: s.color }}
                    x={x}
                    y={y}
                    width={wide}
                    height={Math.max(2, padT + ih - y)}
                    rx={4}
                  />
                  {label ? (
                    <text className="use__tick" x={x + wide / 2} y={y - 7} textAnchor="middle">
                      {money(v)}
                    </text>
                  ) : null}
                </g>
              )
            })
          })
        : series.map((s) => (
            <path
              key={s.name}
              className="use__line"
              style={{ stroke: s.color }}
              d={s.values.map((v, i) => `${i ? 'L' : 'M'}${X(i)},${Y(v)}`).join(' ')}
            />
          ))}

      {/* One strip per bucket, so the whole column answers the pointer rather
          than a 2px line or a 4px dot. */}
      {labels.map((l, i) => {
        const centre = slots || bars ? slotX(i) : X(i)
        const half = iw / Math.max(1, labels.length) / 2
        return (
          <rect
            key={`hit-${i}`}
            className={`use__hit${onPick ? ' use__hit--pick' : ''}`}
            x={centre - half}
            y={padT}
            width={half * 2}
            height={ih}
            onClick={onPick ? () => onPick(i) : undefined}
            onPointerMove={(event) =>
              onHover(event, {
                title: l,
                // Split, only what was spent: eight rows of $0.00 bury the two
                // that matter. The family goes in front of each model's name,
                // because the shade alone does not say which family it is.
                lines: series
                  .filter((s) => series.length === 1 || !s.family || (s.values[i] ?? 0) > 0)
                  .map((s) => ({
                    color: s.color,
                    name:
                      series.length > 1
                        ? s.family && s.family.name !== s.name
                          ? `${s.family.name} · ${s.name}`
                          : s.name
                        : '',
                    value: money(s.values[i] ?? 0),
                  })),
              })
            }
          />
        )
      })}
    </svg>
  )
}

/* ------------------------------------------------------------------ *
 * The person cards and the monthly bills, opened full size
 * ------------------------------------------------------------------ */

interface MonthBill {
  month: string
  live: boolean
  lines: BillLine[]
  total: number
}

const monthLabel = (month: string): string => {
  const d = new Date(`${month.slice(0, 10)}T00:00:00Z`)
  return Number.isNaN(d.getTime())
    ? month
    : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/**
 * Every model's colour: by family, and within a family by how much it spent,
 * the biggest getting the full hue. Models that spent nothing are left out.
 */
function makeShades(spend: Map<string, number>): Map<string, { family: Family; color: string }> {
  const out = new Map<string, { family: Family; color: string }>()
  for (const family of [...FAMILIES, OTHER]) {
    ;[...spend.entries()]
      .filter(([model, cost]) => cost > 0 && familyOf(model) === family)
      .sort((a, b) => b[1] - a[1])
      .forEach(([model], k) => {
        out.set(model, { family, color: family === OTHER ? OTHER.color : shadeOf(family, k) })
      })
  }
  return out
}

/** The page's colours, plus a colour for any model only this view has. */
function withLocalShades(
  page: Map<string, { family: Family; color: string }>,
  rows: { model: string; cost: number }[],
): Map<string, { family: Family; color: string }> {
  const spend = new Map<string, number>()
  for (const row of rows) spend.set(row.model, (spend.get(row.model) ?? 0) + row.cost)
  const local = makeShades(spend)
  return new Map([...local, ...page].filter(([model]) => spend.has(model) || page.has(model)))
}

/** Each day from `start` for `count` days, as ISO date strings (New York's dates, at 00:00 UTC). */
function daysFrom(start: Date, count: number): string[] {
  return Array.from({ length: count }, (_, i) =>
    new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + i)).toISOString(),
  )
}

/** What a card says: a name and a figure. The same on the card and as the view opens. */
function CardFace({ title, value, sub }: { title: string; value: string; sub?: string }) {
  return (
    <span className="use__face">
      <span className="use__face-title">{title}</span>
      <span className="use__face-value">
        {value}
        {sub ? <span className="use__face-sub">{sub}</span> : null}
      </span>
    </span>
  )
}

type HoverFn = (event: React.PointerEvent, next: Omit<Hover, 'x' | 'y'>) => void

/* ------------------------------------------------------------------ *
 * One spend chart for the opened views: bars by day, a trend line
 *
 * Bars are what each day cost -- one white series, or stacked by family with
 * a 2px gap between segments. The line is the 7-day average of the same
 * total, on the same axis (never a second scale), so the trend reads through
 * the day-to-day noise. Drawn with literal colours rather than CSS variables,
 * so the very same SVG can be exported as a picture (exportChart).
 * ------------------------------------------------------------------ */

const INK = '#ffffff'
const INK_2 = '#a6a6a6'
const INK_3 = '#6e6e6e'
const GRID = 'rgba(255, 255, 255, 0.08)'
const AXIS = 'rgba(255, 255, 255, 0.18)'
const SURFACE = '#0a0a0a'
const SINGLE_BAR = 'rgba(255, 255, 255, 0.42)'
const FONT = 'Aptos, "Segoe UI", Helvetica, Arial, sans-serif'

/** A family's colour as a hex string, read from the page's own palette. */
function solidColour(colour: string): string {
  const name = /^var\((--[a-z0-9-]+)\)$/i.exec(colour.trim())?.[1]
  if (!name) return colour
  const from = document.querySelector('.use') ?? document.documentElement
  return getComputedStyle(from).getPropertyValue(name).trim() || '#888888'
}

interface ChartSeries {
  name: string
  colour: string
  values: number[]
}

/** The trailing average over `span` buckets of a run of totals. */
function trailing(totals: number[], span: number): number[] {
  return totals.map((_, i) => {
    const from = Math.max(0, i - span + 1)
    const part = totals.slice(from, i + 1)
    return part.reduce((sum, v) => sum + v, 0) / part.length
  })
}

function roundedTop(x: number, y: number, w: number, h: number, r: number): string {
  const k = Math.max(0, Math.min(r, w / 2, h))
  return `M${x},${y + h}V${y + k}Q${x},${y} ${x + k},${y}H${x + w - k}Q${x + w},${y} ${x + w},${y + k}V${y + h}Z`
}

/** Watch an element's width, for drawing at the size it really is. */
function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const watch = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)))
    watch.observe(node)
    return () => watch.disconnect()
  }, [])
  return [ref, width]
}

const SpendChart = forwardRef<
  SVGSVGElement,
  {
    /** Each bucket's start, as an ISO string: days, months or years. */
    days: string[]
    series: ChartSeries[]
    height: number
    /** What one bar is. Days carry a 7-day average, months a 3-month one, years none. */
    unit?: UsageGrain
    /** The last bucket that has happened; the trend line stops there. All of them when left out. */
    until?: number
    onHover: HoverFn
    onLeave: () => void
  }
>(function SpendChart({ days, series, height, unit = 'day', until, onHover, onLeave }, svgRef) {
  const [box, width] = useWidth<HTMLDivElement>()
  const [at, setAt] = useState<number | null>(null)

  const n = days.length
  const totals = days.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0))
  const span = unit === 'day' ? 7 : unit === 'month' ? 3 : 0
  const average = span ? trailing(totals, span) : []
  const averageName = unit === 'day' ? '7-day average' : '3-month average'
  const barName = unit === 'day' ? 'Daily spend' : unit === 'month' ? 'Monthly spend' : 'Yearly spend'
  const label = (at: string) => dayLabel(at, unit)
  const longLabel = (at: string) => (unit === 'month' ? monthLabel(at) : label(at))
  const solid = series.map((s) => ({ ...s, colour: solidColour(s.colour) }))

  const m = { top: 40, right: 18, bottom: 30, left: 58 }
  const plotW = Math.max(10, width - m.left - m.right)
  const plotH = height - m.top - m.bottom
  const top = niceMax(Math.max(0, ...totals, ...average))
  const y = (v: number) => m.top + plotH - (v / top) * plotH
  const slot = plotW / Math.max(1, n)
  const barW = Math.max(2, Math.min(24, slot * 0.62))
  const cx = (i: number) => m.left + slot * (i + 0.5)
  const every = Math.max(1, Math.ceil(n / Math.max(1, plotW / 54)))
  // The days still to come in a running month have no trend: it stops at today.
  const end = Math.min(n - 1, until ?? n - 1)
  const linePath = average.slice(0, end + 1).map((v, i) => `${i === 0 ? 'M' : 'L'}${cx(i).toFixed(1)},${y(v).toFixed(1)}`).join('')
  const stacked = series.length > 1
  const legend: { name: string; colour: string; line: boolean; x: number }[] = []
  {
    let x = 0
    const entries = [
      ...(stacked ? solid.map((s) => ({ name: s.name, colour: s.colour, line: false })) : [{ name: barName, colour: SINGLE_BAR, line: false }]),
      ...(span ? [{ name: averageName, colour: INK, line: true }] : []),
    ]
    for (const entry of entries) {
      legend.push({ ...entry, x })
      x += (entry.line ? 22 : 16) + entry.name.length * 6.6 + 22
    }
  }

  return (
    <div ref={box} className="use__chart" style={{ height }}>
      {width > 0 ? (
        <svg
          ref={svgRef}
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          fontFamily={FONT}
          onPointerLeave={() => {
            setAt(null)
            onLeave()
          }}
        >
          {/* The legend: always there, so no colour has to be matched by eye. */}
          <g transform={`translate(${m.left}, 12)`} fontSize={12} fill={INK_2}>
            {legend.map((item) => (
              <g key={item.name} transform={`translate(${item.x}, 0)`}>
                {item.line ? (
                  <line x1={0} x2={16} y1={5} y2={5} stroke={INK} strokeWidth={2} strokeLinecap="round" />
                ) : (
                  <rect width={10} height={10} rx={2} fill={item.colour} />
                )}
                <text x={item.line ? 22 : 16} y={9.5}>
                  {item.name}
                </text>
              </g>
            ))}
          </g>

          {/* Grid and the money axis: hairlines, recessive. */}
          {[0, 1, 2, 3, 4].map((k) => {
            const v = (top / 4) * k
            return (
              <g key={k}>
                <line x1={m.left} x2={width - m.right} y1={y(v)} y2={y(v)} stroke={k === 0 ? AXIS : GRID} strokeWidth={1} />
                <text x={m.left - 10} y={y(v) + 4} textAnchor="end" fontSize={11} fill={INK_3} style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {money(v)}
                </text>
              </g>
            )
          })}

          {/* The day being looked at, behind its bar. */}
          {at !== null ? (
            <rect x={m.left + slot * at} y={m.top} width={slot} height={plotH} fill="rgba(255,255,255,0.05)" rx={4} />
          ) : null}

          {/* The bars: grown from the baseline, rounded only at the data end. */}
          {days.map((_, i) => {
            let base = y(0)
            const parts = solid.filter((s) => (s.values[i] ?? 0) > 0)
            return (
              <g key={i}>
                {parts.map((s, k) => {
                  const h = ((s.values[i] ?? 0) / top) * plotH
                  const gap = k > 0 ? 2 : 0
                  const shown = Math.max(0, h - gap)
                  const yTop = base - h
                  base = yTop
                  const last = k === parts.length - 1
                  const fill = stacked ? s.colour : SINGLE_BAR
                  return last ? (
                    <path key={s.name} d={roundedTop(cx(i) - barW / 2, yTop, barW, shown, 4)} fill={fill} />
                  ) : (
                    <rect key={s.name} x={cx(i) - barW / 2} y={yTop} width={barW} height={shown} fill={fill} />
                  )
                })}
              </g>
            )
          })}

          {/* The trend, on the same axis. */}
          {span && end > 0 ? (
            <path d={linePath} fill="none" stroke={INK} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          ) : null}
          {span && end >= 0 ? (
            <circle cx={cx(end)} cy={y(average[end] ?? 0)} r={4} fill={INK} stroke={SURFACE} strokeWidth={2} />
          ) : null}

          {/* Days along the bottom, thinned so they never collide. */}
          {days.map((day, i) =>
            // Every so many days, and the last one -- unless it would sit on top
            // of the one before it.
            i % every === 0 || (i === n - 1 && i % every >= Math.max(2, every * 0.6)) ? (
              <text key={day} x={cx(i)} y={height - 9} textAnchor="middle" fontSize={11} fill={INK_3}>
                {label(day)}
              </text>
            ) : null,
          )}

          {/* Hover targets: the whole height of each day, wider than any bar. */}
          {days.map((day, i) => (
            <rect
              key={`hit-${day}`}
              x={m.left + slot * i}
              y={m.top}
              width={slot}
              height={plotH}
              fill="transparent"
              onPointerMove={(event) => {
                setAt(i)
                onHover(event, {
                  title: longLabel(day),
                  lines: [
                    ...solid
                      .filter((s) => (s.values[i] ?? 0) > 0)
                      .map((s) => ({ color: stacked ? s.colour : SINGLE_BAR, name: stacked ? s.name : 'Spend', value: money(s.values[i] ?? 0) })),
                    ...(stacked ? [{ color: 'transparent', name: 'Total', value: money(totals[i] ?? 0) }] : []),
                    ...(span && i <= end ? [{ color: INK, name: averageName, value: money(average[i] ?? 0) }] : []),
                  ],
                })
              }}
            />
          ))}
        </svg>
      ) : null}
    </div>
  )
})

/** Amounts by name, biggest first, as rows of bars that line up. */
function RankBars({
  items,
  height,
}: {
  items: { name: string; value: number; colour?: string }[]
  height: number
}) {
  const shown = items.filter((one) => one.value > 0).sort((a, b) => b.value - a.value)
  const most = Math.max(1e-9, ...shown.map((one) => one.value))
  return (
    <div className="use__rank" style={{ height }}>
      {shown.length === 0 ? <p className="use__quiet">Nothing in this period.</p> : null}
      {shown.map((one) => (
        <div key={one.name} className="use__rank-row">
          <span className="use__rank-name">{one.name}</span>
          <span className="use__rank-track">
            <span
              className="use__rank-fill"
              style={{ width: `${((one.value / most) * 100).toFixed(1)}%`, background: one.colour ?? SINGLE_BAR }}
            />
          </span>
          <span className="use__rank-value">{money(one.value)}</span>
        </div>
      ))}
    </div>
  )
}

/** The switch both opened views share: everything as one, or by family. */
function SplitSwitch({ split, onSplit }: { split: boolean; onSplit: (split: boolean) => void }) {
  return (
    <div className="use__seg" role="group" aria-label="How to show it">
      <button type="button" aria-pressed={!split} onClick={() => onSplit(false)}>
        All
      </button>
      <button type="button" aria-pressed={split} onClick={() => onSplit(true)}>
        By family
      </button>
    </div>
  )
}

/** How much of an ISO date names a bucket of each size. */
const KEY_LENGTH: Record<UsageGrain, number> = { day: 10, month: 7, year: 4 }

/**
 * Totals per bucket for a set of (day, model, cost) rows: one series, or one
 * per family. Days are added up into months or years by `unit`.
 */
function dailySeries(
  rows: { at: string; model: string; cost: number }[],
  days: string[],
  split: boolean,
  unit: UsageGrain = 'day',
): ChartSeries[] {
  const size = KEY_LENGTH[unit]
  const index = new Map(days.map((day, i) => [day.slice(0, size), i]))
  if (!split) {
    const values = days.map(() => 0)
    for (const row of rows) {
      const i = index.get(row.at.slice(0, size))
      if (i !== undefined) values[i] += row.cost
    }
    return [{ name: 'Spend', colour: SINGLE_BAR, values }]
  }
  return [...FAMILIES, OTHER]
    .map((family) => {
      const values = days.map(() => 0)
      for (const row of rows) {
        if (familyOf(row.model) !== family) continue
        const i = index.get(row.at.slice(0, size))
        if (i !== undefined) values[i] += row.cost
      }
      return { name: family.name, colour: family.color, values }
    })
    .filter((s) => s.values.some((v) => v > 0))
}

/**
 * The chart as a picture, for a report or for finance: the same SVG, drawn
 * three times over onto a canvas with a heading, and saved as PNG. The
 * button will later grow into a proper export for the finance side.
 */
async function exportChart(svg: SVGSVGElement, heading: string, sub: string, file: string): Promise<void> {
  const width = Number(svg.getAttribute('width')) || 1200
  const height = Number(svg.getAttribute('height')) || 400
  const pad = 40
  const head = 86
  const W = width + pad * 2
  const H = height + head + pad
  const inner = new XMLSerializer().serializeToString(svg)
  const escape = (text: string) => text.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string)
  const page =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">` +
    `<rect width="${W}" height="${H}" fill="${SURFACE}"/>` +
    `<text x="${pad}" y="${pad + 14}" font-family='${FONT}' font-size="22" font-weight="600" fill="${INK}">${escape(heading)}</text>` +
    `<text x="${pad}" y="${pad + 40}" font-family='${FONT}' font-size="13" fill="${INK_2}">${escape(sub)}</text>` +
    `<g transform="translate(${pad}, ${head})">${inner}</g></svg>`
  const url = URL.createObjectURL(new Blob([page], { type: 'image/svg+xml;charset=utf-8' }))
  try {
    const image = new Image()
    image.src = url
    await image.decode()
    const scale = 3
    const canvas = document.createElement('canvas')
    canvas.width = W * scale
    canvas.height = H * scale
    const context = canvas.getContext('2d')
    if (!context) throw new Error('No canvas')
    context.scale(scale, scale)
    context.drawImage(image, 0, 0, W, H)
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!png) throw new Error('Could not make the picture')
    const link = document.createElement('a')
    link.href = URL.createObjectURL(png)
    link.download = file
    link.click()
    setTimeout(() => URL.revokeObjectURL(link.href), 2000)
  } finally {
    URL.revokeObjectURL(url)
  }
}

const VIEW_CHART_HEIGHT = 340

/** Each month's first day, oldest first: the `count` months up to and including this one. */
function monthsBack(count: number): string[] {
  const now = newYorkToday()
  return Array.from({ length: count }, (_, i) =>
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - (count - 1) + i, 1)).toISOString(),
  )
}

/** Each year's first day, from the first year with anything in it to this one. */
function yearsOf(rows: { at: string }[]): string[] {
  const now = newYorkToday().getUTCFullYear()
  const first = rows.reduce((min, row) => Math.min(min, Number(row.at.slice(0, 4)) || now), now)
  return Array.from({ length: now - first + 1 }, (_, i) => new Date(Date.UTC(first + i, 0, 1)).toISOString())
}

/** Where the spans a person can be opened at begin. Year means everything. */
const PERSON_SPANS: Record<UsageGrain, { note: string; since: () => string }> = {
  day: { note: 'Last 30 days', since: () => '' },
  month: { note: 'Last 6 months', since: () => monthsBack(6)[0] },
  year: { note: 'All time', since: () => '2020-01-01T00:00:00.000Z' },
}

/**
 * One person, full size: by day over the last 30 days, by month over the last
 * six, or by year over everything (Day / Month / Year, Huanrui, 2026-10-01).
 * The 30 days come with the page; the others are read when first asked for.
 */
function PersonView({
  window: last30,
  email,
  fallback,
  allowance,
  onHover,
  onLeave,
}: {
  window: UsageWindow | null
  email: string
  fallback: UsagePerson | null
  /** This month's allowance, in a line (2026-10-19). */
  allowance?: string
  shades: Map<string, { family: Family; color: string }>
  onHover: HoverFn
  onLeave: () => void
}) {
  const [split, setSplit] = useRemembered('ww-usage-view-split', false)
  const [unit, setUnit] = useState<UsageGrain>('day')
  const [read, setRead] = useState<Partial<Record<UsageGrain, UsageWindow | null>>>({})
  const [state, setState] = useState<'ready' | 'reading' | 'failed'>('ready')
  /** The span drawn: the one asked for once it has been read. */
  const [shown, setShown] = useState<UsageGrain>('day')

  useEffect(() => {
    if (unit === 'day' || unit in read) {
      setState('ready')
      setShown(unit)
      return
    }
    let cancelled = false
    setState('reading')
    withRetry(() => readUsageWindow(PERSON_SPANS[unit].since()))
      .then((found) => {
        if (cancelled) return
        setRead((had) => ({ ...had, [unit]: found }))
      })
      .catch(() => {
        if (!cancelled) setState('failed')
      })
    return () => {
      cancelled = true
    }
  }, [unit, read])

  // While another span is being read, the one already drawn stays up.
  const span = shown === 'day' ? last30 : (read[shown] ?? null)
  const person = span?.people.find((p) => p.email === email) ?? (shown === 'day' ? fallback : null)
  const rows = (span?.personDayModels ?? []).filter((r) => r.email === email)
  const models = (span?.personModels ?? []).filter((r) => r.email === email)
  const today = newYorkToday()
  const buckets =
    shown === 'day'
      ? daysFrom(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 29)), 30)
      : shown === 'month'
        ? monthsBack(6)
        : yearsOf(rows)
  const share = span && person ? Math.round((person.cost / Math.max(1e-9, span.totals.cost)) * 100) : 0
  const byFamily = [...FAMILIES, OTHER].map((family) => ({
    name: family.name,
    value: models.filter((m) => familyOf(m.model) === family).reduce((sum, m) => sum + m.cost, 0),
    colour: solidColour(family.color),
  }))
  const byWhat = shown === 'day' ? 'By day' : shown === 'month' ? 'By month' : 'By year'

  return (
    <div className="use__view">
      <header className="use__view-head">
        <div>
          <h2>{person?.name ?? fallback?.name ?? email}</h2>
          <span className="use__quiet">
            {PERSON_SPANS[shown].note}
            {span ? ` · ${share}% of all spend` : ''}
            {person ? ` · ${person.calls.toLocaleString()} calls` : ''}
            {person?.lastAt ? ` · last on ${dayLabel(person.lastAt, 'day')}` : ''}
          </span>
          {allowance ? <span className="use__quiet use__allowance">{allowance}</span> : null}
        </div>
        <span className="use__view-figure">{money(person?.cost ?? 0)}</span>
        <span className="use__spacer" />
        <div className="use__seg" role="group" aria-label="Period">
          {GRAINS.map((g) => (
            <button key={g.id} type="button" aria-pressed={unit === g.id} onClick={() => setUnit(g.id)}>
              {g.label}
            </button>
          ))}
        </div>
        <SplitSwitch split={split} onSplit={setSplit} />
      </header>
      {state === 'failed' ? (
        <p className="use__quiet">That could not be read. Try again in a moment.</p>
      ) : span ? (
        <div className="use__view-pair" style={state === 'reading' ? { opacity: 0.5 } : undefined}>
          <section className="use__panel">
            <div className="use__label">{byWhat}</div>
            <SpendChart
              days={buckets}
              unit={shown}
              series={dailySeries(rows, buckets, split, shown)}
              height={VIEW_CHART_HEIGHT}
              onHover={onHover}
              onLeave={onLeave}
            />
          </section>
          <section className="use__panel">
            <div className="use__label">{split ? 'By family' : 'By model'}</div>
            <RankBars
              height={VIEW_CHART_HEIGHT}
              items={split ? byFamily : models.map((m) => ({ name: modelName(m.model), value: m.cost }))}
            />
          </section>
        </div>
      ) : state === 'reading' ? (
        <p className="use__quiet" style={{ height: VIEW_CHART_HEIGHT + 40 }}>
          Reading…
        </p>
      ) : (
        <p className="use__quiet">The 30-day view needs docs/usage-window-and-bills.sql to have been run.</p>
      )}
    </div>
  )
}

/** One month's bill, full size: the month as one chart, and what it came to. */
function MonthView({
  bill,
  month,
  months,
  onMonth,
  onHover,
  onLeave,
}: {
  bill: MonthBill | null
  month: string
  /** Every month with a bill, newest first, for the arrows. */
  months: string[]
  onMonth: (month: string) => void
  shades: Map<string, { family: Family; color: string }>
  onHover: HoverFn
  onLeave: () => void
}) {
  const [split, setSplit] = useRemembered('ww-usage-view-split', false)
  const [span, setSpan] = useState<UsageWindow | null>(null)
  const [state, setState] = useState<'reading' | 'ready' | 'failed'>('reading')
  const [exporting, setExporting] = useState(false)
  const chart = useRef<SVGSVGElement>(null)
  /** The month whose figures are drawn; behind `month` while the next is read. */
  const [drawn, setDrawn] = useState(month)

  // Up for the newer month, down for the older one (Huanrui, 2026-10-01).
  const at = months.indexOf(month)
  const newer = at > 0 ? months[at - 1] : null
  const older = at >= 0 && at < months.length - 1 ? months[at + 1] : null
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp' && newer) {
        event.preventDefault()
        onMonth(newer)
      } else if (event.key === 'ArrowDown' && older) {
        event.preventDefault()
        onMonth(older)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [newer, older, onMonth])

  useEffect(() => {
    let cancelled = false
    setState('reading')
    withRetry(() => readUsageMonth(month))
      .then((found) => {
        if (cancelled) return
        setSpan(found.window)
        setDrawn(month)
        setState('ready')
      })
      .catch(() => {
        if (!cancelled) setState('failed')
      })
    return () => {
      cancelled = true
    }
  }, [month])

  // The whole month, every day of it -- the days still to come in the month
  // that is running are there too, empty, so a month always reads the same.
  const start = new Date(`${drawn.slice(0, 10)}T00:00:00Z`)
  const inMonth = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0)).getUTCDate()
  const days = daysFrom(start, inMonth)
  // In the month still running, the last day that has happened.
  const today = newYorkToday().toISOString().slice(0, 10)
  const until = days.findIndex((day) => day.slice(0, 10) === today)
  const series = dailySeries(span?.dayModels ?? [], days, split)
  const models = (span?.dayModels ?? []).reduce((sum, row) => sum + row.cost, 0)

  return (
    <div className="use__view">
      <header className="use__view-head">
        <div className="use__steps" role="group" aria-label="Other months">
          <button type="button" disabled={!newer} onClick={() => newer && onMonth(newer)} aria-label="Newer month" title="Newer month (↑)">
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M2.5 7.5 6 4l3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button type="button" disabled={!older} onClick={() => older && onMonth(older)} aria-label="Older month" title="Older month (↓)">
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        <div>
          <h2>{monthLabel(month)}</h2>
          <span className="use__quiet">{bill?.live ? 'So far this month' : 'The whole month'}</span>
        </div>
        <span className="use__spacer" />
        <SplitSwitch split={split} onSplit={setSplit} />
        <button
          type="button"
          className="use__export"
          disabled={state !== 'ready' || exporting}
          onClick={() => {
            if (!chart.current) return
            setExporting(true)
            void exportChart(
              chart.current,
              `WonderWorks · ${monthLabel(month)}`,
              `Models and pictures by day: ${money(models)} · Whole bill: ${money(bill?.total ?? 0)}`,
              `wonderworks-spend-${month.slice(0, 7)}.png`,
            )
              .catch((error: unknown) => console.warn('the chart could not be exported', error))
              .finally(() => setExporting(false))
          }}
        >
          {exporting ? 'Exporting…' : 'Export image'}
        </button>
      </header>

      <section className="use__panel">
        <div className="use__label">Models and pictures, by day</div>
        {state === 'failed' ? (
          <p className="use__quiet">That month could not be read.</p>
        ) : !span && state === 'reading' ? (
          <p className="use__quiet" style={{ height: VIEW_CHART_HEIGHT + 40 }}>
            Reading…
          </p>
        ) : (
          <div style={state === 'reading' ? { opacity: 0.5, transition: 'opacity 120ms' } : { transition: 'opacity 120ms' }}>
            <SpendChart ref={chart} days={days} series={series} until={until >= 0 ? until : undefined} height={VIEW_CHART_HEIGHT + 40} onHover={onHover} onLeave={onLeave} />
          </div>
        )}
      </section>

      <section className="use__panel">
        <div className="use__label">The bill</div>
        <div className="use__bill-lines">
          {(bill?.lines ?? []).map((line) => (
            <div key={line.item} className="use__bill-line">
              <span>{line.item}</span>
              <b>{money(line.usd)}</b>
            </div>
          ))}
          {/* The month's total, at the end of what makes it up (Huanrui, 2026-10-01). */}
          <div className="use__bill-line use__bill-line--total">
            <span>{bill?.live ? 'Total so far' : 'Total'}</span>
            <b>{money(bill?.total ?? 0)}</b>
          </div>
        </div>
      </section>
    </div>
  )
}
